import { Injectable } from '@angular/core';
import {
  Observable,
  combineLatest,
  switchMap,
  take,
  startWith,
  combineLatestWith,
  map,
  distinctUntilChanged,
  mergeMap,
} from 'rxjs';

import { AccountFacade } from '@mode/accounts/contract';
import { EncodingHelpers, TableEncodingHelpers } from '@mode/charts/util-vegas';
import { DatasetFacade, DatasetPublishingFlagStates, PublishOption, PublishState } from '@mode/dataset/contract';
import { ReportFacade } from '@mode/reports/domain';
import { ChartTypes, DataViewTypes, FeatureFlag, FeatureFlagsFacade, OrgTypes } from '@mode/shared/contract-common';
import { DataViewFacade } from '@mode/switch-views/contract';
import { AnalyticsEvent, AnalyticsService } from '@mode/shared/util-segment';
import { MetaTags, MetaTagsService } from '@mode/shared/util-js';
@Injectable()
export class PlatformHeaderNavPanelComponentFacade {
  public chartFormats$: Observable<{ format: ChartTypes.ChartFormat; fieldsFormat: ChartTypes.FieldFormatMap }>;
  public orgContext$: Observable<OrgTypes.Organization>;
  public canPublishDataset$: Observable<boolean>;
  public publishOptionCTA$: Observable<PublishState>;
  public isDatasetPublished$ = this.datasetFacade.isDatasetPublished$;
  public datasetThoughtspotPublication$ = this.datasetFacade.dataset$.pipe(
    map((dataset) => dataset.details.thoughtspot)
  );
  datasetQueryRunSucceeded$ = this.datasetFacade.dataset$.pipe(
    mergeMap(({ switchView }) => this.dataViewFacade.getQueryRunStateBySwitchViewToken(switchView.token)),
    map((datasetRunState) => datasetRunState === DataViewTypes.QueryRunStatus.Succeeded),
    startWith(false),
    distinctUntilChanged()
  );

  constructor(
    private accountFacade: AccountFacade,
    private datasetFacade: DatasetFacade,
    private dataViewFacade: DataViewFacade,
    private reportFacade: ReportFacade,
    private featureFlagsFacade: FeatureFlagsFacade,
    private analyticsService: AnalyticsService,
    private metaTag: MetaTagsService
  ) {
    this.chartFormats$ = this.datasetFacade.dataset$.pipe(
      switchMap((dataset) => this.dataViewFacade.getModelFormatsByQueryToken(dataset.result.queryToken))
    );
    this.orgContext$ = this.accountFacade.currentOrg$;
    this.publishOptionCTA$ = this.featureFlagsFacade
      .featureValueAsObservable<DatasetPublishingFlagStates>(FeatureFlag.DatasetPublishing)
      .pipe(
        startWith(DatasetPublishingFlagStates.DISABLED),
        combineLatestWith(
          this.accountFacade.currentOrg$,
          this.isDatasetPublished$,
          this.featureFlagsFacade.asObservable(FeatureFlag.DatasetUnpublishing)
        ),
        map(([flag, currentOrg, isPublished, isUnpublishEnabled]) => {
          if (
            !(
              [DatasetPublishingFlagStates.ENABLED, DatasetPublishingFlagStates.ENABLED_ALPHA].includes(flag) &&
              currentOrg.features.tsDatasetsIntegration
            )
          ) {
            return null;
          }
          const canUnpublish = isUnpublishEnabled ? PublishOption.Unpublish : null;
          return isPublished ? canUnpublish : PublishOption.Publish;
        })
      );
    this.canPublishDataset$ = this.publishOptionCTA$.pipe(map((option) => option === PublishOption.Publish));
  }

  createNewReportWithDataset() {
    combineLatest([
      this.accountFacade.defaultCategoricalPalette$,
      this.orgContext$,
      this.datasetFacade.dataset$,
      this.dataViewFacade.model$,
      this.chartFormats$,
    ])
      .pipe(take(1))
      .subscribe(([palette, org, dataset, model, chartFormats]) => {
        const accountId = org.username;
        const enc = EncodingHelpers.initEncoding('table').encoding;
        const encWithColumns = TableEncodingHelpers.addRemainingColumns(enc, model, dataset.result.queryToken);
        this.reportFacade.createReportAndChartFromDatasetEditor(
          accountId,
          dataset.switchView.token,
          palette.token,
          encWithColumns.encoding,
          chartFormats.format,
          chartFormats.fieldsFormat
        );
      });
  }

  openPublishDatasetModal(location?: string) {
    this.datasetFacade.dataset$.pipe(take(1)).subscribe(({ switchView: { token } }) => {
      this.datasetFacade.openPublishDatasetModal(token);
      this.analyticsService.track(AnalyticsEvent.Name.UiClicked, {
        context: this.analyticsService.context,
        location: `${AnalyticsEvent.SubContext.DatasetEditor} ${location}`,
        action: AnalyticsEvent.Name.DatasetPublishLaunched,
        name: this.metaTag.getTag(MetaTags.CurrentUsername),
      });
    });
  }
  openRepublishDatasetModal(location?: string) {
    this.datasetFacade.dataset$.pipe(take(1)).subscribe(({ switchView: { token } }) => {
      this.datasetFacade.openRepublishDatasetModal(token);
      this.analyticsService.track(AnalyticsEvent.Name.UiClicked, {
        context: this.analyticsService.context,
        location: `${AnalyticsEvent.SubContext.DatasetEditor} ${location}`,
        action: AnalyticsEvent.Name.DatasetRepublishLaunched,
        name: this.metaTag.getTag(MetaTags.CurrentUsername),
      });
    });
  }

  openUnpublishDatasetModal(location?: string) {
    this.datasetFacade.openUnpublishDatasetModal();
    const eventData = {
      context: this.analyticsService.context,
      location: `${AnalyticsEvent.SubContext.DatasetEditor} ${location}`,
      action: AnalyticsEvent.Name.DatasetPublishLaunched,
      name: this.metaTag.getTag(MetaTags.CurrentUsername),
    };
    this.analyticsService.track(AnalyticsEvent.Name.UiClicked, eventData);
  }
}
